if (!window.Eurus.loadedScript.has("engine-bundle.js")) {
  window.Eurus.loadedScript.add("engine-bundle.js");

  requestAnimationFrame(() => {
    document.addEventListener("alpine:init", () => {
      Alpine.data("xEngineBundle", () => ({
        // State
        error: false,
        errorMessage: "",
        config: null,
        productData: null,
        componentProductMap: {},   // { handle: { variants: [...], options: [...] } }
        selections: {},                   // { groupId: Set<componentId> }
        componentOpts: {},         // { componentId: { optionName: value } }
        collapsedGroups: {},
        variant: null,
        valid: false,
        bundlePrice: 0,
        compareAtPrice: null,
        savings: 0,
        isFromPrice: false,
        statusMessages: [],
        moneyFormat: null,
        currency: null,
        locale: null,

        // Modal state
        modal: {
          open: false,
          comp: null,
          groupId: null,
          title: "",
          imageUrl: null,
          options: [],
          selections: {},
        },

        // Config
        productId: null,
        productForm: null,

        init() {
          this.productId = this.$el.dataset.productId;
          this.productForm = document.getElementById(this.$el.dataset.formId);
          this.moneyFormat = this.$el.dataset.moneyFormat;
          this.currency = this.$el.dataset.currency || "GBP";
          this.locale = this.$el.dataset.locale || "en";

          if (!this.productForm) {
            console.error("[EngineBundle] Product form not found — cannot initialise");
            this.releaseAtcGate();
            return;
          }

          this.claimAtcGate();

          // Read bundle config from embedded metafield JSON.
          // If missing, skip silently — the bundle still works via the "Included in
          // the bundle" card and the standard product form submit path.
          const configEl = this.$el.querySelector("[data-bundle-config]");
          if (!configEl) {
            console.warn("[EngineBundle] data-bundle-config not found, skipping widget init");
            this.releaseAtcGate();
            return;
          }

          try {
            this.config = JSON.parse(configEl.textContent);
          } catch (e) {
            console.error("[EngineBundle] Failed to parse bundle config:", e);
            this.error = true;
            this.errorMessage = "Failed to load bundle configuration";
            this.releaseAtcGate();
            return;
          }

          // Read embedded product variant data (bundle parent)
          const dataEl = this.$el.querySelector("[data-bundle-product-data]");
          if (dataEl) {
            try {
              const parsed = JSON.parse(dataEl.textContent);
              this.productData = {
                options: parsed.options || [],
                variants: parsed.variants || [],
              };
            } catch (e) {
              console.warn("[EngineBundle] Failed to parse embedded product data:", e);
            }
          }

          // Read embedded component product data (from Liquid all_products lookup)
          const compDataEl = this.$el.querySelector("[data-bundle-component-products]");
          if (compDataEl) {
            try {
              this.componentProductMap = JSON.parse(compDataEl.textContent);
            } catch (e) {
              console.warn("[EngineBundle] Failed to parse component product data:", e);
            }
          }

          // Normalize component handles in config
          this.normalizeComponentHandles();

          const atcButton = this.productForm.querySelector('button[type="submit"]');
          if (atcButton) {
            atcButton.addEventListener(
              "click",
              (e) => {
                if (!this.valid) {
                  e.preventDefault();
                  e.stopImmediatePropagation();
                  return false;
                }
              },
              { capture: true }
            );
          }

          // Intercept form submit for validation
          this.productForm.addEventListener("submit", (e) => {
            if (!this.valid) {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          });

          // Bridge: dropdowns rendered in the MFS-249 card (outside this widget's
          // Alpine scope) dispatch this event on change — forward to the option handler.
          this.$el.addEventListener("card-option-change", (e) => {
            const { compId, optName, value } = e.detail || {};
            if (compId == null || !optName) return;
            this.onComponentOptionChange(compId, optName, value);
          });

          // Attach change listeners to any card dropdowns rendered outside this Alpine scope
          // (e.g. Age Size selects in the "Included in the bundle" card). Uses data attributes
          // to avoid HTML-attribute quote-escaping issues with inline onchange handlers.
          document.querySelectorAll("[data-bundle-card-opt]").forEach((el) => {
            el.addEventListener("change", () => {
              const compId = parseInt(el.dataset.bundleCompId, 10);
              const optName = el.dataset.bundleOptName;
              if (!compId || !optName) return;
              this.onComponentOptionChange(compId, optName, el.value);
            });
          });

          document.querySelectorAll("[data-bundle-card-comp]").forEach((el) => {
            const openFromCard = (event) => {
              const compId = parseInt(el.dataset.bundleCardComp, 10);
              const groupId = parseInt(el.dataset.bundleCardGroup, 10);
              if (!compId || !groupId) return;
              this.onComponentCardClick(groupId, compId, event);
            };
            el.addEventListener("click", openFromCard);
            el.addEventListener("keydown", (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openFromCard(event);
              }
            });
          });

          // Init and run first update
          this.initSelections();
          this.update();
        },

        /**
         * Ensure each component in config has a handle accessible via comp.handle
         * (metafield may store it as product_handle or handle).
         */
        normalizeComponentHandles() {
          for (const group of this.config.component_groups || []) {
            for (const comp of group.components || []) {
              if (!comp.handle && comp.product_handle) {
                comp.handle = comp.product_handle;
              }
            }
          }
        },

        // ── Selections ──

        initSelections() {
          const groups = this.config.component_groups || [];
          for (const g of groups) {
            this.selections[g.id] = new Set();

            // Auto-select when all components are required
            const comps = g.components || [];
            if (g.min_selections === comps.length && g.max_selections === comps.length) {
              for (const c of comps) {
                this.selections[g.id].add(String(c.id));
              }
            }

            // Collapse by default if configured
            if (g.collapsed_by_default) {
              this.collapsedGroups[g.id] = true;
            }
          }
        },

        onComponentChangeByIds(groupId, compId, event) {
          if (!this.setComponentSelection(groupId, compId, event)) return;

          const comp = this.findComp(compId);
          if (comp && this.componentHasOptions(comp)) {
            this.openModal(comp, groupId);
          }

          this.update();
        },

        onRadioCardClick(groupId, compId) {
          const group = this.findGroup(groupId);
          const comp = this.findComp(compId);
          if (!group || !comp) return;

          const input = this.$el.querySelector(`#engine-comp-${groupId}-${compId}`);
          if (input?.disabled) return;

          const lockKey = `${groupId}-${compId}`;
          if (this._radioClickLock === lockKey) return;
          this._radioClickLock = lockKey;
          requestAnimationFrame(() => {
            if (this._radioClickLock === lockKey) this._radioClickLock = null;
          });

          if (input) input.checked = true;
          this.setComponentSelection(groupId, compId, { target: { checked: true } });

          if (this.componentHasOptions(comp)) {
            this.openModal(comp, groupId);
          }

          this.update();
        },

        onComponentCardClick(groupId, compId, event) {
          if (event?.target?.closest("select, [data-bundle-card-opt]")) return;

          const group = this.findGroup(groupId);
          const comp = this.findComp(compId);
          if (!group || !comp) return;

          const cid = String(compId);
          if (!this.selections[groupId]) this.selections[groupId] = new Set();

          if (this.getInputType(group) === "radio") {
            this.selections[groupId].forEach((oldCid) => {
              if (oldCid !== cid) delete this.componentOpts[oldCid];
            });
            this.selections[groupId].clear();
          }

          this.selections[groupId].add(cid);

          if (this.componentHasOptions(comp)) {
            this.openModal(comp, groupId);
          }

          this.update();
        },

        setComponentSelection(groupId, compId, event) {
          const group = this.findGroup(groupId);
          const cid = String(compId);
          if (!group) return false;

          if (!this.selections[groupId]) this.selections[groupId] = new Set();

          if (this.getInputType(group) === "radio") {
            this.selections[groupId].forEach((oldCid) => {
              if (oldCid !== cid) delete this.componentOpts[oldCid];
            });
            this.selections[groupId].clear();
            this.selections[groupId].add(cid);
            this.syncRadioInputs(groupId, cid);
          } else if (event?.target?.checked) {
            if (group.max_selections && this.selections[groupId].size >= group.max_selections) {
              if (event.target) event.target.checked = false;
              return false;
            }
            this.selections[groupId].add(cid);
          } else {
            this.selections[groupId].delete(cid);
            delete this.componentOpts[compId];
          }

          return true;
        },

        syncRadioInputs(groupId, selectedCid) {
          this.$el.querySelectorAll(`input[name="bundle-group-${groupId}"]`).forEach((input) => {
            input.checked = input.value === String(selectedCid);
          });
        },

        onDropdownChange(groupId, value) {
          if (!this.selections[groupId]) this.selections[groupId] = new Set();

          // MFS-389: dropdown-display groups select component options via the
          // inline option dropdown(s) rendered below the selector — NOT the modal
          // (radio/grid groups still use the modal). Switching the chosen
          // component clears the previous one's option selections.
          this.selections[groupId].forEach((oldCid) => {
            if (oldCid !== String(value)) delete this.componentOpts[oldCid];
          });
          this.selections[groupId].clear();

          if (value) {
            this.selections[groupId].add(String(value));
          }

          this.update();
        },

        onComponentOptionChange(compId, optName, value) {
          if (!this.componentOpts[compId]) this.componentOpts[compId] = {};
          if (value) {
            this.componentOpts[compId][optName] = value;
          } else {
            delete this.componentOpts[compId][optName];
          }
          this.update();
        },

        // ── Modal ──

        openModal(comp, groupId) {
          const handle = comp.handle || comp.product_handle;
          const liveData = handle ? this.componentProductMap[handle] || null : null;
          const options = this.resolveComponentOptions(comp, liveData);

          if (!options.length) return;

          const cardEl =
            this.$el.querySelector(`[data-comp-id="${comp.id}"]`) ||
            document.querySelector(`[data-bundle-card-comp="${comp.id}"]`);
          const title =
            cardEl?.querySelector("[data-comp-title]")?.textContent?.trim() ||
            comp.display_label ||
            comp.product_title ||
            "Select options";
          const imgEl = cardEl?.querySelector("img");
          const imageUrl = imgEl?.src || comp.product_image_url || null;

          const prev = this.componentOpts[comp.id] || {};

          this.modal = {
            open: true,
            comp,
            groupId,
            title,
            imageUrl,
            options,
            selections: { ...prev },
          };
        },

        onModalOptionChange(optName, value) {
          if (value) {
            this.modal.selections[optName] = value;
          } else {
            delete this.modal.selections[optName];
          }
        },

        get modalAllSelected() {
          if (!this.modal.open || !this.modal.options.length) return false;
          return this.modal.options.every((opt) => this.modal.selections[opt.name]);
        },

        closeModal(confirmed) {
          if (confirmed) {
            const compId = this.modal.comp.id;
            this.componentOpts[compId] = { ...this.modal.selections };
            this.update();
          } else if (!confirmed && this.modal.comp) {
            const compId = String(this.modal.comp.id);
            const gid = this.modal.groupId;
            if (!this.componentOpts[this.modal.comp.id] && this.selections[gid]) {
              this.selections[gid].delete(compId);
              const input = this.$el.querySelector(`#engine-comp-${gid}-${compId}`);
              if (input) input.checked = false;
            }
            this.update();
          }

          this.modal = { open: false, comp: null, groupId: null, title: "", imageUrl: null, options: [], selections: {} };
        },

        // ── Inline component options (dropdown-display groups) ──

        /**
         * Resolved option groups (e.g. Size) for a selected component, used to
         * render the inline option dropdowns for dropdown-display groups. Works
         * for both exposed PIM options and options derived from a multi-variant
         * component product. Writes to componentOpts via onComponentOptionChange.
         * (Radio/grid groups still use the pop-up modal.)
         */
        resolvedOptionsFor(compId) {
          const comp = this.findComp(compId);
          if (!comp) return [];
          const handle = comp.handle || comp.product_handle;
          const liveData = handle ? this.componentProductMap[handle] || null : null;
          return this.resolveComponentOptions(comp, liveData);
        },

        // ── Update cycle ──

        update() {
          this.validate();
          this.matchVariant();
          this.calculatePrice();
          this.updateFormState();
          this.updateFormInputs();
          this.broadcastState();
        },

        /** State the personalisation preview follows (MFYEB-186). */
        bundleState() {
          const oosIds = [];
          for (const g of this.config?.component_groups || []) {
            for (const c of g.components || []) {
              if (this.isComponentOosByHandle(c.handle || c.product_handle)) oosIds.push(Number(c.id));
            }
          }
          return {
            productId: this.productId,
            selectedIds: this.selectedComps().map((c) => Number(c.id)),
            oosIds,
            valid: !!this.valid && !!this.variant,
            bundlePrice: this.bundlePrice,
          };
        },

        broadcastState() {
          document.dispatchEvent(new CustomEvent("engine-bundle:updated", { detail: this.bundleState() }));
        },

        validate() {
          const messages = [];
          let isValid = true;

          for (const g of this.config.component_groups || []) {
            if (!g.is_required) continue;
            const count = this.selections[g.id] ? this.selections[g.id].size : 0;
            if (count < (g.min_selections || 0)) {
              isValid = false;
              const name = (g.name || "").replace(/:+\s*$/, "");
              if (g.min_selections === g.max_selections) {
                messages.push(name + ": select " + g.min_selections);
              } else {
                messages.push(name + ": select at least " + g.min_selections);
              }
            }
          }

          // Check multi-variant components have options selected (silent — dropdown is in the card)
          for (const comp of this.selectedComps()) {
            if (!this.componentOptionsComplete(comp)) {
              isValid = false;
            }
          }

          // Data integrity: every selected component must resolve to live product
          // data with at least one purchasable variant. Missing data means the
          // published bundle references a product the storefront can no longer
          // see (handle changed, unpublished, deleted since the last bundle
          // sync) — without this gate resolveComponentVariants() silently drops
          // the component and the customer buys an incomplete bundle at full
          // price. Treat it like out of stock and explain in the console.
          const dataIssues = this.collectComponentDataIssues();
          if (dataIssues.length) {
            isValid = false;
            this.logComponentDataIssues(dataIssues);
          }

          this.valid = isValid;
          this.statusMessages = messages;
        },

        collectComponentDataIssues() {
          const issues = [];

          // A required group in which NO option is purchasable is a dead end
          // the customer cannot resolve — detect it at page load rather than
          // waiting for a selection that can never succeed. Also catches
          // required groups misconfigured with zero components.
          for (const g of this.config.component_groups || []) {
            if (!g.is_required) continue;
            const comps = g.components || [];

            if (!comps.length) {
              issues.push({
                group_id: g.id,
                group: g.name,
                reason: "required group has no components configured — fix the bundle in the engine",
              });
              continue;
            }

            // Skip groups whose components are all selected — the selected-
            // components loop below reports each of those with a more precise
            // reason, and reporting the group as well would state the same
            // fact twice.
            const allSelected = comps.every((comp) => this.selections[g.id]?.has(String(comp.id)));
            const allDead = comps.every((comp) => this.isComponentOosByHandle(comp.handle || comp.product_handle));

            if (allDead && !allSelected) {
              issues.push({
                group_id: g.id,
                group: g.name,
                reason: "required group has no purchasable options — every component product is out of stock, unpublished, deleted, or missing from the last bundle sync",
              });
            }
          }

          for (const comp of this.selectedComps()) {
            const title = comp.display_label || comp.product_title || "component " + comp.id;
            const handle = comp.handle || null;

            if (!handle) {
              issues.push({
                component_id: comp.id,
                title: title,
                handle: null,
                reason: "bundle config carries no product handle for this component — re-sync the bundle in the engine",
              });
              continue;
            }

            const liveData = this.componentProductMap[handle];

            if (!liveData || !liveData.variants || !liveData.variants.length) {
              issues.push({
                component_id: comp.id,
                title: title,
                handle: handle,
                reason: "no storefront data for this handle — the product was unpublished, deleted, or its handle changed since the bundle was last synced; re-sync the bundle in the engine",
              });
              continue;
            }

            if (liveData.variants.every((v) => v.available === false)) {
              issues.push({
                component_id: comp.id,
                title: title,
                handle: handle,
                reason: "all variants are out of stock",
              });
              continue;
            }

            // Selected option values must match a live variant. Without this,
            // resolveComponentVariants()'s first-available fallback silently
            // substitutes a DIFFERENT variant when the product's option values
            // changed since the bundle was last synced — wrong item shipped,
            // no error anywhere.
            const compOpts = this.componentOpts[comp.id];
            if (compOpts && Object.keys(compOpts).length) {
              const liveOptions = liveData.options || [];
              const matches = liveData.variants.filter((variant) => {
                for (let oi = 0; oi < liveOptions.length; oi++) {
                  const optName =
                    typeof liveOptions[oi] === "object" ? liveOptions[oi].name : liveOptions[oi];
                  const optKey = "option" + (oi + 1);
                  if (compOpts[optName] && variant[optKey] !== compOpts[optName]) return false;
                }
                return true;
              });

              if (!matches.length) {
                issues.push({
                  component_id: comp.id,
                  title: title,
                  handle: handle,
                  selected_options: compOpts,
                  reason: "selected options no longer match any variant — the product's options or values changed since the bundle was last synced; re-sync the bundle in the engine",
                });
              } else if (matches.every((v) => v.available === false)) {
                issues.push({
                  component_id: comp.id,
                  title: title,
                  handle: handle,
                  selected_options: compOpts,
                  reason: "selected variant is out of stock",
                });
              }
            }
          }

          return issues;
        },

        logComponentDataIssues(issues) {
          const signature = JSON.stringify(issues);
          if (this._loggedIssuesSignature === signature) return;
          this._loggedIssuesSignature = signature;

          for (const issue of issues) {
            console.debug(
              "[EngineBundle] Blocking add to cart — bundle " + (this.productId || "?") +
              " component unresolvable:",
              issue
            );
          }
        },

        matchVariant() {
          if (!this.productData) {
            this.variant = null;
            return;
          }

          const variants = this.productData.variants || [];

          if (variants.length === 1) {
            this.variant = variants[0];
          } else {
            this.variant = null;
            for (const v of variants) {
              if (v.available !== false) {
                this.variant = v;
                break;
              }
            }
          }

          if (this.variant) {
            const variantInput = this.productForm?.querySelector('input[name="id"]');
            if (variantInput) variantInput.value = this.variant.id;
          }
        },

        calculatePrice() {
          const pricing = this.config.pricing || {};
          const model = pricing.model;
          const selected = this.selectedComps();
          const fullySelected = this.isFullySelected();

          if (model === "dynamic" && !fullySelected) {
            this.bundlePrice = this.calculateFromPrice();
            this.isFromPrice = true;
          } else {
            this.bundlePrice = this.calculateBundlePrice(selected);
            this.isFromPrice = false;
          }

          const compareAt = this.getCompareAtPrice();
          this.compareAtPrice = compareAt;
          this.savings = compareAt != null ? Math.max(0, compareAt - this.bundlePrice) : 0;
        },

        getVariantPrice() {
          if (!this.variant) return 0;
          const price = this.variant.price;
          if (price == null) return 0;
          return price / 100;
        },

        getVariantCompareAtPrice() {
          if (!this.variant) return null;
          const cap = this.variant.compare_at_price;
          if (cap == null || cap <= 0) return null;
          return cap / 100;
        },

        calculateBundlePrice(selectedComponents) {
          const pricing = this.config.pricing || {};
          const model = pricing.model;

          switch (model) {
            case "fixed":
              return this.getVariantPrice();

            case "dynamic":
              return this.dynamicTotal(selectedComponents);

            default:
              return this.getVariantPrice();
          }
        },

        dynamicTotal(selectedComponents) {
          const livePrices = this.componentProductMap;
          let total = 0;

          for (const comp of selectedComponents) {
            let price = null;
            const handle = comp.handle;

            if (handle && livePrices[handle]?.variants) {
              const variants = livePrices[handle].variants;
              if (variants.length === 1) {
                price = variants[0].price / 100;
              } else if (variants.length > 1) {
                const compOpts = this.componentOpts[comp.id];
                if (compOpts) {
                  const liveOptions = livePrices[handle].options || [];
                  for (const variant of variants) {
                    let match = true;
                    for (let oi = 0; oi < liveOptions.length; oi++) {
                      const optName = typeof liveOptions[oi] === "object" ? liveOptions[oi].name : liveOptions[oi];
                      const optKey = "option" + (oi + 1);
                      if (compOpts[optName] && variant[optKey] !== compOpts[optName]) {
                        match = false;
                        break;
                      }
                    }
                    if (match) {
                      price = variant.price / 100;
                      break;
                    }
                  }
                }
                if (price == null) price = variants[0].price / 100;
              }
            }

            if (price == null) price = 0;

            total += price * (comp.quantity || 1);
          }

          return total;
        },

        getCompareAtPrice() {
          const model = this.config.pricing?.model;
          if (model === "fixed") {
            return this.getVariantCompareAtPrice();
          }
          if (model === "percentage" || model === "calculated") {
            return this.dynamicTotal(this.selectedComps());
          }
          return null;
        },

        calculateFromPrice() {
          const livePrices = this.componentProductMap;
          const groups = this.config.component_groups || [];
          let total = 0;

          for (const g of groups) {
            if (!g.is_required) continue;
            const comps = g.components || [];
            const minSelections = g.min_selections || 1;

            const prices = comps.map((comp) => {
              const handle = comp.handle;
              let cheapest = null;

              if (handle && livePrices[handle]?.variants?.length) {
                for (const v of livePrices[handle].variants) {
                  const vPrice = v.price / 100;
                  if (cheapest == null || vPrice < cheapest) cheapest = vPrice;
                }
              }

              if (cheapest == null) cheapest = 0;
              return cheapest * (comp.quantity || 1);
            });

            prices.sort((a, b) => a - b);
            for (let s = 0; s < Math.min(minSelections, prices.length); s++) {
              total += prices[s];
            }
          }

          return total;
        },

        isFullySelected() {
          for (const g of this.config.component_groups || []) {
            if (!g.is_required) continue;
            const count = this.selections[g.id] ? this.selections[g.id].size : 0;
            if (count < (g.min_selections || 1)) return false;
          }
          return true;
        },

        // ATC gate (see snippets/product-template.liquid). Claiming hands the button to
        // updateFormState(); releasing restores it so a failed init can't block purchase.
        atcGateButtons() {
          const scope = this.productForm || document;
          return Array.from(scope.querySelectorAll("[data-atc-gate]"));
        },

        claimAtcGate() {
          for (const btn of this.atcGateButtons()) {
            btn.dataset.atcGate = "active";
          }
        },

        releaseAtcGate() {
          for (const btn of this.atcGateButtons()) {
            const state = btn.dataset.atcGate;
            if (state !== "pending" && state !== "active") continue;
            btn.dataset.atcGate = "released";
            btn.disabled = false;
            btn.setAttribute("aria-disabled", "false");
          }
        },

        updateFormState() {
          const atcButton = this.productForm?.querySelector('button[type="submit"]');
          if (!atcButton) return;

          const hasVariant = !!this.variant;
          const bundleOos = !!document.querySelector('[data-bundle-oos="true"]');
          const ready = this.valid && hasVariant && !bundleOos;

          atcButton.disabled = !ready;
          atcButton.setAttribute("aria-disabled", String(!ready));
        },

        updateFormInputs() {
          if (!this.productForm) return;

          this.productForm
            .querySelectorAll("[data-bundle-property]")
            .forEach((el) => el.remove());

          if (!this.variant) return;

          const props = this.buildLineItemProperties();
          for (const key in props) {
            if (!props.hasOwnProperty(key)) continue;
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = "properties[" + key + "]";
            input.value = props[key];
            input.setAttribute("data-bundle-property", "");
            this.productForm.appendChild(input);
          }

          // Cart-level attributes — survive the cart transform expand operation so the
          // selected variant/component IDs remain queryable at the order level (line
          // item properties on the parent bundle line are dropped when it is expanded).
          const cartAttrs = {
            _bundle_id: props._bundle_id,
          };
          if (props._bundle_components) {
            cartAttrs._bundle_components = props._bundle_components;
          }
          for (const key in cartAttrs) {
            if (!cartAttrs.hasOwnProperty(key) || !cartAttrs[key]) continue;
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = "attributes[" + key + "]";
            input.value = cartAttrs[key];
            input.setAttribute("data-bundle-property", "");
            this.productForm.appendChild(input);
          }
        },

        buildLineItemProperties() {
          const props = {};
          const bundle = this.config;

          props["_bundle_id"] = String(bundle.id || this.productId);
          props["_bundle_name"] = bundle.name || "";

          const components = this.resolveComponentVariants();
          if (components.length) {
            props["_bundle_components"] = JSON.stringify(components);
          }

          const selected = this.selectedComps();
          props["_bundle_price"] = String(this.calculateBundlePrice(selected));

          // Customer-visible properties: group name → selected component + option values
          const optionKeys = [];
          for (const g of bundle.component_groups || []) {
            const groupSelected = this.selections[g.id];
            if (!groupSelected?.size) continue;

            const labels = [];
            for (const c of g.components || []) {
              if (!groupSelected.has(String(c.id))) continue;

              // Get title from rendered card
              const cardEl = this.$el.querySelector(`[data-comp-id="${c.id}"]`);
              let label = cardEl?.querySelector("[data-comp-title]")?.textContent?.trim() || c.display_label || c.product_title || "";

              const compOpts = this.componentOpts[c.id];
              if (compOpts) {
                const optParts = Object.values(compOpts);
                if (optParts.length) label += " - " + optParts.join(", ");
              }

              labels.push(label);
            }

            if (labels.length) {
              props[g.name] = labels.join("; ");
              optionKeys.push(g.name);
            }
          }

          // The order confirmation email lists the child lines itself, so it needs to tell the
          // group properties apart from the personalisation ones. Their values are not a reliable
          // signal: the personalisation widget writes a technical key and a label key whose values
          // differ for select fields.
          if (optionKeys.length) {
            props["_bundle_option_keys"] = optionKeys.join("|~K~|");
          }

          return props;
        },

        resolveComponentVariants() {
          const result = [];
          const selected = this.selectedComps();

          for (const comp of selected) {
            const handle = comp.handle;
            const liveData = handle ? this.componentProductMap[handle] : null;
            if (!liveData?.variants?.length) continue;

            const variants = liveData.variants;
            let matchedVariant = null;

            if (variants.length === 1) {
              matchedVariant = variants[0];
            } else {
              const compOpts = this.componentOpts[comp.id];
              if (compOpts) {
                const liveOptions = liveData.options || [];
                for (const variant of variants) {
                  let match = true;
                  for (let oi = 0; oi < liveOptions.length; oi++) {
                    const optName =
                      typeof liveOptions[oi] === "object" ? liveOptions[oi].name : liveOptions[oi];
                    const optKey = "option" + (oi + 1);
                    if (compOpts[optName] && variant[optKey] !== compOpts[optName]) {
                      match = false;
                      break;
                    }
                  }
                  if (match) {
                    matchedVariant = variant;
                    break;
                  }
                }
              }
            }

            // Safety net: never silently drop a selected component. If nothing
            // matched (e.g. before a size is chosen), use the first available
            // variant. ATC stays gated by validate() until required options are
            // chosen, so the customer's selected size is what is ultimately submitted.
            if (!matchedVariant && variants.length) {
              matchedVariant = variants.find((v) => v.available !== false) || variants[0];
            }

            if (matchedVariant) {
              result.push({
                variant_id: "gid://shopify/ProductVariant/" + matchedVariant.id,
                quantity: comp.quantity || 1,
                component_id: comp.id,
                price: (matchedVariant.price / 100).toFixed(2),
              });
            }
          }

          return result;
        },

        // ── Helpers ──

        selectedComps() {
          const result = [];
          for (const g of this.config.component_groups || []) {
            const selected = this.selections[g.id] || new Set();
            for (const c of g.components || []) {
              if (selected.has(String(c.id))) result.push(c);
            }
          }
          return result;
        },

        findGroup(groupId) {
          for (const g of this.config.component_groups || []) {
            if (g.id === groupId || String(g.id) === String(groupId)) return g;
          }
          return null;
        },

        findComp(compId) {
          for (const g of this.config.component_groups || []) {
            for (const c of g.components || []) {
              if (String(c.id) === String(compId)) return c;
            }
          }
          return null;
        },

        getInputType(group) {
          const displayType = (group.display_type || "").toLowerCase();
          if (displayType === "radio") return "radio";
          if (Number(group.max_selections) === 1) return "radio";
          if (displayType === "checkbox") return "checkbox";
          return "checkbox";
        },

        isSelected(groupId, compId) {
          return this.selections[groupId]?.has(String(compId)) || false;
        },

        isAllIncluded(group) {
          const comps = group.components || [];
          return group.min_selections === comps.length && group.max_selections === comps.length;
        },

        isComponentOosByHandle(handle) {
          // Fail closed: a component the storefront cannot resolve (no handle,
          // unpublished/deleted product, no variants) is just as unpurchasable
          // as one that is out of stock — rendering it selectable only leads
          // the customer to a blocked add-to-cart with no visible reason.
          if (!handle) return true;
          const live = this.componentProductMap[handle];
          if (!live?.variants?.length) return true;
          return live.variants.every((v) => !v.available);
        },

        componentHasOptions(comp) {
          const handle = comp.handle || comp.product_handle;
          const liveData = handle ? this.componentProductMap[handle] : null;
          const resolved = this.resolveComponentOptions(comp, liveData);
          return resolved.length > 0;
        },

        componentOptionsComplete(comp) {
          const handle = comp.handle || comp.product_handle;
          const liveData = handle ? this.componentProductMap[handle] : null;
          const options = this.resolveComponentOptions(comp, liveData);
          if (!options.length) return true;
          const selected = this.componentOpts[comp.id] || {};
          return options.every((opt) => !!selected[opt.name]);
        },

        resolveComponentOptions(comp, liveData) {
          const liveVariants = liveData?.variants || [];
          const liveOptions = liveData?.options || [];
          const options = comp.options || [];
          const exposed = options.filter((o) => o.is_exposed);

          // Fallback: when PIM exposes no options (is_exposed), derive the
          // selectable options from the component product's own variants (e.g.
          // Size). NOTE: this intentionally ignores is_exposed — a multi-variant
          // component with no exposed options still gets a selector. Correct when
          // size is a real customer choice (the robe bundles, MFS-389); if a bundle
          // ever needs a multi-variant component pinned to a fixed variant, this
          // fallback would need gating.
          if (!exposed.length && liveVariants.length > 1) {
            const derived = [];
            for (let optIdx = 1; optIdx <= 3; optIdx++) {
              const optKey = "option" + optIdx;
              const seen = {};
              const uniqueVals = [];
              for (const v of liveVariants) {
                const ov = v[optKey];
                if (ov && !seen[ov]) {
                  seen[ov] = true;
                  uniqueVals.push(ov);
                }
              }
              if (!uniqueVals.length) continue;
              if (uniqueVals.length === 1 && uniqueVals[0] === "Default Title") continue;

              const optName =
                liveOptions.length >= optIdx
                  ? typeof liveOptions[optIdx - 1] === "object"
                    ? liveOptions[optIdx - 1].name
                    : liveOptions[optIdx - 1]
                  : "Option " + optIdx;

              derived.push({
                name: optName,
                position: optIdx,
                values: uniqueVals.map((val) => ({
                  value: val,
                  oos: this.isOptionValueOos(optName, val, optIdx, liveVariants),
                })),
              });
            }
            return derived;
          }

          return exposed.map((opt, i) => ({
            name: opt.name || opt.display_name,
            position: opt.position || i + 1,
            values: (opt.values || []).map((val) => {
              const v = typeof val === "object" ? val.value : val;
              return {
                value: v,
                oos: this.isOptionValueOos(opt.name, v, opt.position || i + 1, liveVariants),
              };
            }),
          }));
        },

        isOptionValueOos(optionName, value, optPosition, liveVariants) {
          if (!liveVariants?.length) return false;
          const optKey = "option" + optPosition;
          const matching = liveVariants.filter((v) => v[optKey] === value);
          if (!matching.length) return true;
          return matching.every((v) => !v.available);
        },

        getConstraintText(group) {
          const min = group.min_selections || 0;
          const max = group.max_selections || 0;
          if (min === max && min > 0) return "Select " + min;
          if (min > 0 && max > 0) return "Select " + min + "-" + max;
          if (min > 0) return "Select at least " + min;
          if (max > 0) return "Up to " + max;
          return "";
        },

        getSelectedTagText(compId) {
          const opts = this.componentOpts[compId];
          if (!opts) return "";
          return Object.values(opts).join(", ");
        },

        toggleGroup(groupId) {
          this.collapsedGroups[groupId] = !this.collapsedGroups[groupId];
        },

        formatPrice(amount) {
          try {
            const code = this.currency || "GBP";
            return new Intl.NumberFormat(navigator.language || "en-GB", {
              style: "currency",
              currency: code,
            }).format(amount);
          } catch (e) {
            return "£" + parseFloat(amount).toFixed(2);
          }
        },
      }));
    });
  });
}
